import {
  createRootRoute,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { ClerkLoaded, ClerkLoading, Show } from '@clerk/react';
import { ThemeProvider } from '../hooks/useTheme';
import { Win95Desktop } from '../components/desktop/Win95Desktop';
import { SignUpWindow } from '../components/auth/SignUpWindow';
import { AdminConsentLandingBanner } from '../components/onboard/AdminConsentLandingBanner';
import {
  OnboardingCompletedOverlay,
  SigninWelcomeBarrier,
} from '../components/onboard/OnboardingCompletedOverlay';
import { requestAttention } from '../lib/attention/attention';
import { useCurrentUser } from '../lib/useCurrentUser';
import { testIds } from '../lib/testIds';

/**
 * Routes that render without requiring a Clerk sign-in. /trust/* surface is
 * a customer-facing transparency surface (decisions.md §D4) and must be
 * reachable signed-out for the public sub-routes (architecture,
 * data-residency, overview). The sub-route components themselves decide
 * whether to show "sign in to see your data" copy.
 */
function isPublicPath(pathname: string): boolean {
  // /sign-in renders Clerk's prebuilt `<SignIn />` for returning
  // visitors who clicked the "Sign in" cross-link on the sign-up
  // card. Must bypass the sign-in gate or SignUpWindow would render
  // over the top, defeating the cross-link.
  //
  // Repancy is sign-up-first as of 2026-05-30: `/` mounts <SignUp />
  // via SignUpWindow, and /sign-in is the secondary route.
  //
  // /share/<token> is the Phase 6 invitation trust page; B2B recipients
  // hit it after Microsoft redeems the invitation, so it MUST be public
  // (they're not signed in to Clerk).
  //
  // /learn/<...> is the marketing-tier explainer route family.
  return (
    pathname === '/trust' ||
    pathname.startsWith('/trust/') ||
    pathname === '/sign-in' ||
    pathname.startsWith('/sign-in/') ||
    pathname.startsWith('/share/') ||
    pathname.startsWith('/learn/')
  );
}

const ORIENTATION_FLAG = 'repancy-orientation-completed';

function hasCompletedOrientation(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(ORIENTATION_FLAG) === '1';
  } catch {
    return true;
  }
}

function RootComponent() {
  // useCurrentUser combines Clerk auth + convex/users row existence.
  // isLoading stays true until the Clerk → /clerk-users-webhook upsert
  // lands, so the SPA never tries to read api.users.current and get null.
  // See src/lib/useCurrentUser.ts (verbatim from Convex docs).
  const { isLoading, isAuthenticated } = useCurrentUser();
  const isSignedIn = isAuthenticated;
  const isLoaded = !isLoading;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const publicPath = isPublicPath(pathname);
  const firstTimeRedirectedRef = useRef<boolean>(false);

  // Orientation redirect goes through the attention orchestrator so
  // it serializes correctly with the FirstRunTour (priority 10) and
  // the portal-welcome overlay (default priority 50). The sign-in
  // welcome flourish is NOT here — it renders synchronously via
  // <SigninWelcomeBarrier /> below, which blocks the first paint
  // BEFORE Win95Desktop ever mounts. Using the orchestrator for
  // sign-in welcome produced a desktop-flash-then-overlay sequence
  // because `requestAttention` is called from useEffect, which runs
  // after React's first commit.
  //
  // firstTimeRedirectedRef is belt-and-suspenders so we don't enqueue
  // duplicate orientation requests within this React mount; the
  // orchestrator dedupes by id but skipping the enqueue is cleaner.
  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) return;
    if (publicPath) return;
    if (firstTimeRedirectedRef.current) return;
    firstTimeRedirectedRef.current = true;

    void requestAttention({
      id: 'orientation-redirect',
      priority: 0,
      shouldFire: () => !hasCompletedOrientation(),
      run: async () => {
        try {
          window.localStorage.setItem(ORIENTATION_FLAG, '1');
        } catch {
          /* incognito / quota; non-fatal */
        }
        await navigate({
          to: '/',
          search: { onboard: 'auto' },
          replace: true,
        });
      },
    });
  }, [isLoaded, isSignedIn, publicPath, navigate]);

  // Public /trust routes render without the desktop chrome and don't
  // require sign-in.
  if (publicPath) {
    return <Outlet />;
  }

  // May 2026 Clerk-recommended pattern (`<Show>` is the Core 3
  // replacement for `<SignedIn>` / `<SignedOut>`). `<Show>` renders
  // null while Clerk is still booting (e.g. during the
  // `__clerk_handshake` round-trip on sign-in / sign-out) — we wrap
  // it in `<ClerkLoaded>` so the explicit "Clerk is mid-boot" branch
  // is visible, with `<ClerkLoading>` rendering the AuthLayout
  // gradient as a continuous backdrop so the user never sees a blank
  // white flash during the handshake.
  //
  // We deliberately do NOT gate this on `useCurrentUser` (Convex
  // users.current row) — that was producing a second blank flash
  // after Clerk loaded but before the /clerk-users-webhook upsert
  // landed. The Convex row gate moves DOWN to the orientation
  // useEffect above, which is the only top-level surface that
  // actually needs it.
  return (
    <>
      <ClerkLoading>
        <div
          data-testid={testIds.auth.loadingScreen}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            background:
              'linear-gradient(135deg, #0a0a0a 0%, #111118 50%, #1a1a2e 100%)',
          }}
        />
      </ClerkLoading>
      <ClerkLoaded>
        <Show
          when="signed-in"
          fallback={
            <div data-testid={testIds.auth.signInPage}>
              <SignUpWindow />
            </div>
          }
        >
          <ThemeProvider>
            <Win95Desktop>
              <Outlet />
            </Win95Desktop>
            {/* F18.next — catches Microsoft's admin-consent redirect
                (?admin_consent=True&tenant=...) and routes the user back to
                /onboard/new to resume the wizard. Renders nothing when the
                URL doesn't carry the query string, so it's a free addition. */}
            <AdminConsentLandingBanner />
            {/* XP-style welcome overlay played when the "Open my portal"
                buttons fire (mid-session attention moment routed through
                the orchestrator). Renders nothing until the orchestrator
                marks portal-welcome current. */}
            <OnboardingCompletedOverlay />
            {/* Sign-in welcome barrier — reads sessionStorage synchronously
                on first render, blocks the desktop's first paint with the
                XP welcome overlay, unmounts after ~3s. Separate path from
                the orchestrator because that path produced a
                desktop-flash-then-overlay sequence. Renders null on
                subsequent loads in the same browser session. */}
            <SigninWelcomeBarrier />
          </ThemeProvider>
        </Show>
      </ClerkLoaded>
    </>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
